// Safe when this module is imported during vite.config load (import.meta.env may be unset).
export const BASE_URL =
  (typeof import.meta !== "undefined" && import.meta.env?.BASE_URL) || "/";

export const DEFAULT_SERVICE_SLUG = "web-development";

const SERVICE_SLUG_ALIASES = {
  "saas-development": "app-development",
  "blockchain-solution": "cyber-security",
  blockchain: "cyber-security",
  cybersecurity: "cyber-security",
};

/** Map legacy or shorthand service slugs to the canonical page slug. */
export function canonicalServiceSlug(slug = DEFAULT_SERVICE_SLUG) {
  const key = String(slug || DEFAULT_SERVICE_SLUG).toLowerCase();
  return SERVICE_SLUG_ALIASES[key] || key;
}

function normalizedBase() {
  return BASE_URL.endsWith("/") ? BASE_URL : `${BASE_URL}/`;
}

/** Public file under BASE_URL, e.g. Taldel Black.jpg → /Taldel%20Black.jpg */
export function publicAsset(file) {
  const clean = String(file || "")
    .replace(/^\/+/, "")
    .split("/")
    .filter(Boolean)
    .map(encodeURIComponent)
    .join("/");
  return `${normalizedBase()}${clean}`;
}

/** Absolute app path for a page, e.g. / or /services/web-development */
export function pagePath(page = "home") {
  const base = normalizedBase();
  if (!page || page === "home") return base;
  return `${base}${String(page).replace(/^\/+|\/+$/g, "")}`;
}

/**
 * Current route info from the URL path.
 * @returns {{ page: "home" | "service" | "product" | "product-detail" | "insights" | "insight-detail" | "signup" | "login" | "about" | "privacy" | "terms", slug: string | null }}
 */
export function getRouteInfo() {
  const base = normalizedBase().replace(/\/$/, "");
  let path = window.location.pathname || "/";

  if (base && path.toLowerCase().startsWith(base.toLowerCase())) {
    path = path.slice(base.length) || "/";
  }

  const parts = path
    .replace(/^\/+/, "")
    .replace(/\/+$/, "")
    .split("/")
    .filter(Boolean)
    .filter((part) => part !== "index.html");

  if (!parts.length) {
    return { page: "home", slug: null };
  }

  if (parts[0].toLowerCase() === "services") {
    return {
      page: "service",
      slug: canonicalServiceSlug(parts[1] || DEFAULT_SERVICE_SLUG),
    };
  }

  const first = parts[0].toLowerCase();
  if (
    first === "our-portfolios" ||
    first === "our-products" ||
    first === "product" ||
    first === "products" ||
    first === "portfolio" ||
    first === "portfolios"
  ) {
    if (parts[1]) {
      return { page: "product-detail", slug: parts[1].toLowerCase() };
    }
    return { page: "product", slug: null };
  }

  if (first === "about-us" || first === "about" || first === "company") {
    return { page: "about", slug: null };
  }

  if (first === "signup" || first === "register") {
    return { page: "signup", slug: null };
  }

  if (first === "login" || first === "signin" || first === "sign-in") {
    return { page: "login", slug: null };
  }

  if (first === "privacy-policy" || first === "privacy") {
    return { page: "privacy", slug: null };
  }

  if (first === "terms-and-conditions" || first === "terms" || first === "terms-of-service") {
    return { page: "terms", slug: null };
  }

  if (first === "insights") {
    if (parts[1]) {
      return { page: "insight-detail", slug: parts[1].toLowerCase() };
    }
    return { page: "insights", slug: null };
  }

  return { page: "home", slug: null };
}

/** @deprecated Prefer getRouteInfo(). Kept for simple home checks. */
export function getCurrentPage() {
  return getRouteInfo().page === "service" ? "service" : "home";
}

/** In-app section link on the home page, e.g. /#contact */
export function appRoute(section) {
  const id = section.replace(/^#\/?/, "");
  return `${normalizedBase()}#${id}`;
}

/** Page link, optionally with a section hash */
export function pageRoute(page, section = "") {
  const path = pagePath(page);
  if (!section) return path;
  const id = section.replace(/^#\/?/, "");
  return `${path}#${id}`;
}

/** Service page link, e.g. /services/web-development#faqs */
export function serviceRoute(slug = DEFAULT_SERVICE_SLUG, section = "") {
  return pageRoute(`services/${canonicalServiceSlug(slug || DEFAULT_SERVICE_SLUG)}`, section);
}

/** Section id from a hash or hash-only route. Paths without a hash are not sections. */
export function sectionId(route = "") {
  if (!route) return "";
  const hashIndex = route.lastIndexOf("#");
  if (hashIndex >= 0) return route.slice(hashIndex + 1);
  if (route.startsWith("#")) return route.replace(/^#\/?/, "");
  return "";
}
